import { useCallback, useEffect, useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useWebsocket } from "@finitless/shared/contexts";
import { useBrand } from "@finitless/shared/contexts";
import { useSmartPagination } from "./useSmartPagination";

/**
 * Hook for handling real-time conversation updates via websockets
 * Integrates with React Query for cache updates
 */
export const useConversationSocketUpdates = (options = {}) => {
  const queryClient = useQueryClient();
  const { isConnected, registerEventListener, unregisterEventListener } =
    useWebsocket();
  const { selectedBrand } = useBrand();
  const [listenersSetup, setListenersSetup] = useState(false);
  const hookId = useRef(
    `useConversationSocketUpdates-${Date.now()}-${Math.random()}`,
  );

  // Smart pagination integration
  const smartPagination = useSmartPagination(["conversations"], {
    itemsPerPage: options.itemsPerPage || 10,
    onNewItemAdded: options?.onNewItemAdded || (() => {}),
    onItemUpdated: options?.onItemUpdated || (() => {}),
    onItemRemoved: options?.onItemRemoved || (() => {}),
  });

  /**
   * Update conversation in cache
   */
  const updateConversationInCache = useCallback(
    (updatedConversation) => {
      // Update all conversation queries that might contain this conversation
      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) return oldData;

        const updatedConversations = oldData.conversations.map(
          (conversation) =>
            conversation.id === updatedConversation.id
              ? { ...conversation, ...updatedConversation }
              : conversation,
        );

        return {
          ...oldData,
          conversations: updatedConversations,
        };
      });

      // Also update individual conversation queries if they exist
      queryClient.setQueryData(
        ["conversation", updatedConversation.id],
        updatedConversation,
      );
    },
    [queryClient],
  );

  /**
   * Add new conversation to cache
   */
  const addConversationToCache = useCallback(
    (conversationData) => {
      // Only process conversations for the current brand
      if (
        selectedBrand?.id &&
        conversationData.business_id !== selectedBrand.id
      ) {
        return;
      }

      queryClient.setQueriesData({ queryKey: ["conversations"] }, (oldData) => {
        if (!oldData?.conversations) {
          return {
            conversations: [conversationData],
            pagination: { total: 1, page: 1, page_size: 10 },
          };
        }

        // Add new conversation to the beginning of the list
        const updatedConversations = [
          conversationData,
          ...oldData.conversations,
        ];

        return {
          ...oldData,
          conversations: updatedConversations,
          pagination: {
            ...oldData.pagination,
            total: oldData.pagination.total + 1,
          },
        };
      });

      smartPagination?.handleItemAdded?.(conversationData);
    },
    [selectedBrand?.id, queryClient, smartPagination],
  );

  /**
   * Handle new conversation
   */
  const handleNewConversation = useCallback(
    (conversationData) => {
      // Only process conversations for the current brand
      if (
        selectedBrand?.id &&
        conversationData.business_id !== selectedBrand.id
      ) {
        return;
      }

      addConversationToCache(conversationData);
    },
    [selectedBrand?.id, addConversationToCache],
  );

  /**
   * Handle conversation updates
   */
  const handleConversationUpdate = useCallback(
    (conversationData) => {
      // Only process conversations for the current brand
      if (
        selectedBrand?.id &&
        conversationData.business_id !== selectedBrand.id
      ) {
        return;
      }

      updateConversationInCache(conversationData);
      smartPagination.handleItemUpdated(conversationData);
    },
    [selectedBrand?.id, updateConversationInCache, smartPagination],
  );

  /**
   * Handle new conversation messages
   */
  const handleNewConversationMessage = useCallback(
    (messageData) => {
      // Add the new message to the conversation messages cache
      if (messageData.conversation_id) {
        queryClient.setQueryData(
          ["conversationMessages", messageData.conversation_id],
          (oldData) => {
            if (!oldData?.messages) {
              return {
                messages: [messageData],
                pagination: { total: 1, page: 1, page_size: 50 },
              };
            }

            // Check if message already exists to avoid duplicates
            const messageExists = oldData.messages.some(
              (msg) => msg.id === messageData.id,
            );
            if (messageExists) {
              return oldData;
            }

            // Add new message to the end of the list
            const updatedMessages = [...oldData.messages, messageData];

            return {
              ...oldData,
              messages: updatedMessages,
              pagination: {
                ...oldData.pagination,
                total: oldData.pagination.total + 1,
              },
            };
          },
        );

        // Also update the conversation's last_message_at and messages_count
        queryClient.setQueriesData(
          { queryKey: ["conversations"] },
          (oldData) => {
            if (!oldData?.conversations) return oldData;

            const updatedConversations = oldData.conversations.map(
              (conversation) => {
                if (conversation.id === messageData.conversation_id) {
                  return {
                    ...conversation,
                    last_message_at: messageData.created_at,
                    messages_count: (conversation.messages_count || 0) + 1,
                  };
                }
                return conversation;
              },
            );

            // Re-sort by last_message_at descending (most recent first)
            updatedConversations.sort((a, b) => {
              const dateA = new Date(a.last_message_at || a.created_at);
              const dateB = new Date(b.last_message_at || b.created_at);
              return dateB - dateA;
            });

            return {
              ...oldData,
              conversations: updatedConversations,
            };
          },
        );

        // Update individual conversation query
        queryClient.setQueryData(
          ["conversation", messageData.conversation_id],
          (oldConversation) => {
            if (!oldConversation) return oldConversation;

            return {
              ...oldConversation,
              last_message_at: messageData.created_at,
              messages_count: (oldConversation.messages_count || 0) + 1,
            };
          },
        );
      }
    },
    [queryClient],
  );

  // Use refs to maintain stable references
  const handleNewConversationRef = useRef(handleNewConversation);
  const handleConversationUpdateRef = useRef(handleConversationUpdate);
  const handleNewConversationMessageRef = useRef(handleNewConversationMessage);

  // Update refs when handlers change
  useEffect(() => {
    handleNewConversationRef.current = handleNewConversation;
    handleConversationUpdateRef.current = handleConversationUpdate;
    handleNewConversationMessageRef.current = handleNewConversationMessage;
  }, [
    handleNewConversation,
    handleConversationUpdate,
    handleNewConversationMessage,
  ]);

  /**
   * Setup socket listeners using centralized event system
   */
  const setupSocketListeners = useCallback(() => {
    if (!isConnected || listenersSetup) return;

    // Register with centralized event system
    registerEventListener(
      "conversation.created",
      handleNewConversation,
      hookId.current,
    );
    registerEventListener(
      "conversation.updated",
      handleConversationUpdate,
      hookId.current,
    );
    registerEventListener(
      "conversation.message.created",
      handleNewConversationMessage,
      hookId.current,
    );

    setListenersSetup(true);
  }, [
    isConnected,
    selectedBrand?.id,
    handleNewConversation,
    handleConversationUpdate,
    handleNewConversationMessage,
    listenersSetup,
    registerEventListener,
  ]);

  /**
   * Cleanup socket listeners using centralized event system
   */
  const cleanupSocketListeners = useCallback(() => {
    if (!listenersSetup) return;

    // Unregister from centralized event system
    unregisterEventListener("conversation.created", hookId.current);
    unregisterEventListener("conversation.updated", hookId.current);
    unregisterEventListener("conversation.message.created", hookId.current);

    setListenersSetup(false);
  }, [listenersSetup, unregisterEventListener]);

  // Setup listeners when connected
  useEffect(() => {
    if (isConnected && !listenersSetup) {
      setupSocketListeners();
    }
  }, [isConnected, setupSocketListeners]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      cleanupSocketListeners();
    };
  }, [cleanupSocketListeners]);

  return {
    isConnected,
    listenersSetup,
    // Manual control methods
    setupSocketListeners,
    cleanupSocketListeners,
    // Cache update methods (for manual use)
    updateConversationInCache,
    addConversationToCache,
    // Smart pagination integration
    smartPagination,
  };
};

export default useConversationSocketUpdates;
